'use client';

import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { CloudOff, Loader2 } from 'lucide-react';
import { CardWrapper } from '@/components/ui/card-wrapper';
import { H2, Text } from '@/components/ui/typography';
import {
  BackendUnavailableError,
  getBackendAvailabilityPingUrl,
  isApiRequestUrl,
} from '@/lib/backend-maintenance';
import { cn } from '@/lib/utils';

let nativeFetch: typeof fetch | null = null;

function getNativeFetch(): typeof fetch {
  if (typeof window === 'undefined') {
    return fetch;
  }
  if (!nativeFetch) {
    nativeFetch = window.fetch.bind(window);
  }
  return nativeFetch;
}

export function BackendMaintenanceGate({ children }: { children: ReactNode }) {
  const [showOverlay, setShowOverlay] = useState(false);
  /** Synced with overlay; read synchronously inside patched fetch (no stale closure). */
  const overlayRef = useRef(false);

  const setBackendDown = useCallback((down: boolean) => {
    const wasShowing = overlayRef.current;
    overlayRef.current = down;
    setShowOverlay(down);
    // Full reload so contexts and dashboard data refetch after an outage (blocked requests left stale state).
    if (wasShowing && !down && typeof window !== 'undefined') {
      window.location.reload();
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const original = getNativeFetch();

    const patched: typeof fetch = async (input, init) => {
      const url =
        typeof input === 'string'
          ? input
          : input instanceof URL
            ? input.href
            : input.url;

      if (!isApiRequestUrl(url)) {
        return original(input, init);
      }

      // Already in maintenance: block further API traffic (stops retry / effect storms).
      if (overlayRef.current) {
        return Promise.reject(new BackendUnavailableError());
      }

      try {
        const response = await original(input, init);
        if (response.status === 502 || response.status === 503 || response.status === 504) {
          setBackendDown(true);
        } else {
          setBackendDown(false);
        }
        return response;
      } catch (err) {
        setBackendDown(true);
        throw err;
      }
    };

    window.fetch = patched;

    return () => {
      window.fetch = original;
    };
  }, [setBackendDown]);

  // While overlay is shown, poll solo /health until the service is back.
  useEffect(() => {
    if (!showOverlay) return;

    const pingUrl = getBackendAvailabilityPingUrl();
    if (!pingUrl) return;

    const tick = async () => {
      try {
        const res = await getNativeFetch()(pingUrl, {
          method: 'GET',
          cache: 'no-store',
        });
        if (res.ok) {
          setBackendDown(false);
        }
      } catch {
        // still down
      }
    };

    void tick();
    const id = window.setInterval(tick, 4000);
    return () => window.clearInterval(id);
  }, [showOverlay, setBackendDown]);

  return (
    <>
      {children}
      {showOverlay ? (
        <div
          className={cn(
            'fixed inset-0 z-[100] flex items-center justify-center',
            'bg-background/85 backdrop-blur-sm',
            'pointer-events-auto'
          )}
          role="alert"
          aria-live="assertive"
        >
          <CardWrapper className="max-w-md text-center shadow-lg" hover={false}>
            <div className="flex flex-col items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                <CloudOff className="h-8 w-8 text-muted-foreground" aria-hidden />
              </div>
              <div className="space-y-2">
                <H2 className="text-xl">Quick maintenance</H2>
                <Text variant="secondary">
                  We can&apos;t reach the server right now — usually this lasts only a second or two during an
                  update. This page will refresh automatically when we&apos;re back.
                </Text>
              </div>
              <div className="flex items-center gap-2 text-caption text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                <span>Checking connection…</span>
              </div>
            </div>
          </CardWrapper>
        </div>
      ) : null}
    </>
  );
}
